import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Filter, Package, Clock, Sparkles, Wheat, CheckCircle, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const categories = [
    { id: 'all', label: 'All Products' },
    { id: '1121 Basmati', label: '1121 Basmati' },
    { id: '1509 Basmati', label: '1509 Basmati' },
    { id: 'Sugandha', label: 'Sugandha' },
    { id: 'Sharbati', label: 'Sharbati' },
    { id: '1401 Basmati', label: '1401 Basmati' },
    { id: 'Pusa Basmati', label: 'Pusa Basmati' },
    { id: '1718 Basmati', label: '1718 Basmati' },
    { id: 'Traditional', label: 'Traditional' },
    { id: 'Non-Basmati', label: 'Non-Basmati' },
    { id: 'Pesticide Free', label: 'Pesticide Free' }
  ];

  const packagingOptions = [
    { size: '1 kg', description: 'Retail packs', icon: Package },
    { size: '5 kg', description: 'Family packs', icon: Package },
    { size: '10 kg', description: 'Bulk retail', icon: Package },
    { size: '25 kg', description: 'Commercial', icon: Package },
    { size: '50 kg', description: 'Industrial', icon: Package },
    { size: 'Custom', description: 'Your specification', icon: Package }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  return (
    <main data-testid="products-page">
      {/* Hero Section */}
      <section
        data-testid="products-hero"
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1586201375761-83865001e31c?w=1920')`
          }}
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 pt-24 pb-12 text-center">
          <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block animate-fade-up">
            Our Collection
          </span>
          <h1
            data-testid="products-title"
            className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-up delay-100"
          >
            Premium <span className="text-gold">Basmati</span> Rice
          </h1>
          <p className="font-body text-lg md:text-xl text-white/80 max-w-2xl mx-auto animate-fade-up delay-200">
            Explore our carefully curated selection of the finest basmati rice varieties
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-cream-muted sticky top-0 z-30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-navy flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category.id}
                data-testid={`filter-${category.id}`}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 font-body text-sm tracking-widest uppercase whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-navy text-white'
                    : 'bg-cream text-gray-600 hover:bg-cream-muted'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section data-testid="products-grid" className="py-24 md:py-32 wheat-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-gray-600">Loading products...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    data-testid={`product-card-${index}`}
                    className="bg-white group cursor-pointer overflow-hidden border border-transparent hover:border-gold transition-all duration-500 shadow-sm hover:shadow-xl"
                    onClick={() => setSelectedProduct(product)}
                  >
                    {/* Image */}
                    <div className="h-56 overflow-hidden bg-cream-muted relative">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-navy/90 text-white text-xs tracking-widest uppercase font-body">
                          {product.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="font-heading text-2xl font-semibold text-navy mb-3 group-hover:text-gold transition-colors">
                        {product.name}
                      </h3>
                      <p className="font-body text-gray-600 mb-4 line-clamp-2">
                        {product.description}
                      </p>

                      {/* Specs */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="text-center p-2 bg-cream">
                          <Wheat className="w-4 h-4 text-gold mx-auto mb-1" />
                          <span className="font-body text-xs text-gray-600">{product.grain_length}</span>
                        </div>
                        <div className="text-center p-2 bg-cream">
                          <Clock className="w-4 h-4 text-gold mx-auto mb-1" />
                          <span className="font-body text-xs text-gray-600">{product.cooking_time}</span>
                        </div>
                        <div className="text-center p-2 bg-cream">
                          <Sparkles className="w-4 h-4 text-gold mx-auto mb-1" />
                          <span className="font-body text-xs text-gray-600">{product.aroma}</span>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.features.slice(0, 3).map((feature, fIndex) => (
                          <span
                            key={fIndex}
                            className="px-2 py-1 bg-gold/10 text-gold text-xs tracking-wider font-body"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      <button className="w-full py-3 border border-navy text-navy font-body text-sm tracking-widest uppercase group-hover:bg-navy group-hover:text-white transition-all duration-300 flex items-center justify-center gap-2">
                        View Details
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredProducts.length === 0 && (
                <div className="text-center py-12">
                  <Wheat className="w-16 h-16 text-gold/30 mx-auto mb-4" />
                  <p className="font-body text-gray-600">No products found in this category</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div
          data-testid="product-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={e => e.stopPropagation()}
          >
            <button
              data-testid="close-modal"
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-navy text-white flex items-center justify-center hover:bg-gold transition-colors"
            >
              <X size={20} />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Image */}
              <div className="h-64 md:h-full">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                <span className="px-3 py-1 bg-gold/10 text-gold text-xs tracking-widest uppercase font-body mb-4 inline-block">
                  {selectedProduct.category}
                </span>
                <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
                  {selectedProduct.name}
                </h2>
                <p className="font-body text-gray-600 mb-6">
                  {selectedProduct.description}
                </p>

                {/* Specifications */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between py-2 border-b border-cream-muted">
                    <span className="font-body text-gray-600">Grain Length</span>
                    <span className="font-body font-semibold text-navy">{selectedProduct.grain_length}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-cream-muted">
                    <span className="font-body text-gray-600">Cooking Time</span>
                    <span className="font-body font-semibold text-navy">{selectedProduct.cooking_time}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-cream-muted">
                    <span className="font-body text-gray-600">Aroma</span>
                    <span className="font-body font-semibold text-navy">{selectedProduct.aroma}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="font-heading text-lg font-semibold text-navy mb-3">Features</h4>
                  <div className="space-y-2">
                    {selectedProduct.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-gold" />
                        <span className="font-body text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  to="/contact"
                  data-testid="product-inquiry-btn"
                  className="w-full py-3 bg-navy text-white font-body text-sm tracking-widest uppercase flex items-center justify-center gap-2 hover:bg-navy-dark transition-colors"
                >
                  Inquire Now
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Packaging Options */}
      <section data-testid="packaging-options" className="py-24 md:py-32 diagonal-pattern relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Flexible Solutions
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
              Packaging Options
            </h2>
            <div className="gold-line bg-gold mx-auto mb-6" />
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              From retail-ready packs to bulk industrial quantities, we offer flexible packaging 
              solutions to meet your market needs
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {packagingOptions.map((option, index) => (
              <div
                key={index}
                data-testid={`packaging-${index}`}
                className="bg-cream p-6 text-center group hover:bg-navy hover:text-white transition-all duration-300"
              >
                <Package className="w-10 h-10 mx-auto mb-4 text-navy group-hover:text-gold transition-colors" />
                <div className="font-heading text-2xl font-bold mb-1 group-hover:text-gold transition-colors">
                  {option.size}
                </div>
                <div className="font-body text-sm text-gray-600 group-hover:text-white/70 transition-colors">
                  {option.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Label */}
      <section data-testid="private-label" className="py-24 md:py-32 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                White Label Solutions
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
                Private Labelling<br />
                <span className="text-gold">Services</span>
              </h2>
              <div className="gold-line bg-gold mb-8" />
              <p className="font-body text-lg text-white/80 mb-8">
                Build your brand with our premium rice. We offer comprehensive private labelling 
                services including custom packaging design, brand printing, and flexible order 
                quantities to help you establish your presence in the market.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Custom packaging design and printing',
                  'Your brand logo and product information',
                  'Multiple packaging sizes available',
                  'Minimum order quantities from 1 container',
                  'Full documentation and certification support'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" />
                    <span className="font-body text-white/80">{item}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                data-testid="private-label-cta"
                className="inline-flex items-center gap-3 bg-gold text-navy-dark px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-gold-light transition-colors"
              >
                Discuss Your Requirements
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm p-8 border border-white/20 text-center">
                  <div className="font-heading text-4xl font-bold text-gold mb-2">500+</div>
                  <div className="font-body text-sm text-white/70">Private Label Clients</div>
                </div>
                <img
                  src="https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400"
                  alt="Rice packaging"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://images.unsplash.com/photo-1705147289789-6df2593f1b1e?w=400"
                  alt="Premium rice"
                  className="w-full h-48 object-cover"
                />
                <div className="bg-white/10 backdrop-blur-sm p-8 border border-white/20 text-center">
                  <div className="font-heading text-4xl font-bold text-gold mb-2">100%</div>
                  <div className="font-body text-sm text-white/70">Customizable</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="products-cta" className="py-24 md:py-32 leaf-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
            Need Samples or Quotation?
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            Contact our team for product samples, bulk pricing, and customized solutions 
            tailored to your specific requirements.
          </p>
          <Link
            to="/contact"
            data-testid="products-contact-btn"
            className="group inline-flex items-center gap-3 bg-navy text-white px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-navy-dark transition-colors"
          >
            Request Quote
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Products;
